import type { Editor } from '@tiptap/core'
import { useEffect, useReducer, useRef, useState } from 'react'
import styled from 'styled-components'

import Icon from '../../general/Icon'
import Modal from '../../molecules/Modal'
import { AaFormattingDropdown } from './AaFormattingDropdown'
import type { BlockStyle } from './BlockStyleDropdown'
import { BlockStyleDropdown } from './BlockStyleDropdown'

type Props = {
  editor: Editor
  blockStyles?: BlockStyle[]
}

/**
 * Toolbar for TipTap — parity with legacy Lexical toolbar (undo/redo, block style
 * incl. lists, B/I/U, link, “Aa" extra marks). Styled with theme tokens.
 */
export function TipTapToolbar({ editor, blockStyles }: Props) {
  const [, rerender] = useReducer((n: number) => n + 1, 0)
  const [linkModalOpen, setLinkModalOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const handle = () => rerender()
    editor.on('transaction', handle)
    return () => {
      editor.off('transaction', handle)
    }
  }, [editor])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (linkModalOpen) {
      const id = setTimeout(() => inputRef.current?.focus(), 50)
      return () => clearTimeout(id)
    }
  }, [linkModalOpen])

  const canUndo = editor.can().undo()
  const canRedo = editor.can().redo()

  const openLinkModal = () => {
    const existing = editor.getAttributes('link').href as string | undefined
    setLinkUrl(existing ?? '')
    setLinkModalOpen(true)
  }

  const confirmLink = () => {
    const trimmed = linkUrl.trim()
    if (trimmed === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
    } else {
      const hasProtocol = /^[a-z][a-z\d+.-]*:/i.test(trimmed)
      const href = hasProtocol ? trimmed : `https://${trimmed}`
      editor.chain().focus().extendMarkRange('link').setLink({ href }).run()
    }
    setLinkModalOpen(false)
  }

  const removeLink = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run()
    setLinkModalOpen(false)
  }

  return (
    <Toolbar role="toolbar" aria-label="Formatting">
      <ToolbarButton
        type="button"
        title="Undo"
        disabled={!canUndo}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <Icon name="UndoOutlined" />
      </ToolbarButton>
      <ToolbarButton
        type="button"
        title="Redo"
        disabled={!canRedo}
        onClick={() => editor.chain().focus().redo().run()}
      >
        <Icon name="RedoOutlined" />
      </ToolbarButton>

      <ToolbarSeparator aria-hidden />
      <BlockStyleDropdown editor={editor} allowedStyles={blockStyles} />
      <ToolbarSeparator aria-hidden />

      <ToolbarButton
        type="button"
        title="Bold"
        $active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <strong>B</strong>
      </ToolbarButton>
      <ToolbarButton
        type="button"
        title="Italic"
        $active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <em>I</em>
      </ToolbarButton>
      <ToolbarButton
        type="button"
        title="Underline"
        $active={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <UnderlineSpan>U</UnderlineSpan>
      </ToolbarButton>

      <ToolbarSeparator aria-hidden />
      <ToolbarButton
        type="button"
        title={editor.isActive('link') ? 'Edit link' : 'Add link'}
        $active={editor.isActive('link')}
        onClick={openLinkModal}
      >
        <Icon name="LinkOutlined" />
      </ToolbarButton>
      <AaFormattingDropdown editor={editor} />

      <Modal
        classicPadding
        closeButton
        isModalVisible={linkModalOpen}
        setIsModalVisible={setLinkModalOpen}
        localisation={{
          title: <LinkModalTitle>Insert link</LinkModalTitle>,
          confirmText: 'Apply',
          cancelText: 'Cancel'
        }}
        onConfirm={confirmLink}
        onCancel={() => setLinkModalOpen(false)}
        testId="rich-editor-link"
      >
        <LinkModalBody>
          <LinkLabel htmlFor="rich-editor-link-input">URL</LinkLabel>
          <LinkInput
            id="rich-editor-link-input"
            ref={inputRef}
            type="url"
            placeholder="https://"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') confirmLink()
            }}
          />
          {editor.isActive('link') && (
            <RemoveLinkButton type="button" onClick={removeLink}>
              Remove link
            </RemoveLinkButton>
          )}
        </LinkModalBody>
      </Modal>
    </Toolbar>
  )
}

const Toolbar = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.color.neutral.color10};
  border-bottom: 1px solid ${({ theme }) => theme.color.neutral.color7};
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 6px;
  padding: 6px 8px;
`

const ToolbarButton = styled.button<{ $active?: boolean }>`
  align-items: center;
  background: ${({ $active }) =>
    $active ? 'rgba(0, 0, 0, 0.08)' : 'transparent'};
  border-radius: 4px;
  border: 1px solid transparent;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  font-size: 14px;
  justify-content: center;
  min-height: 32px;
  min-width: 32px;
  padding: 4px 10px;

  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.06);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`
const ToolbarSeparator = styled.span`
  background: ${({ theme }) => theme.color.neutral.color7};
  display: inline-block;
  height: 22px;
  margin: 0 4px;
  width: 1px;
`

const UnderlineSpan = styled.span`
  text-decoration: underline;
`

const LinkModalTitle = styled.span`
  color: ${({ theme }) => theme.color.neutral.color2};
  display: block;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.3;
  text-transform: none !important;
`

const LinkModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const LinkLabel = styled.label`
  color: ${({ theme }) => theme.color.neutral.color3};
  font-size: 16px;
  font-weight: 500;
`

const LinkInput = styled.input`
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.color.neutral.color7};
  color: ${({ theme }) => theme.color.neutral.color2};
  font-size: 14px;
  padding: 8px 10px;
  width: 100%;

  &:focus {
    border-color: ${({ theme }) => theme.color.status.primary};
    outline: none;
  }
`

const RemoveLinkButton = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  color: ${({ theme }) => theme.color.status.danger};
  cursor: pointer;
  font-size: 16px;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`
